<i18n>
{
  "de": {
    "perMonth": "/ Monat",
    "active": "Aktiv",
    "pending": "Ausstehend",
    "cancelled": "Gekündigt",
    "subscribe": "Beitreten",
    "manage": "Verwalten",
    "accessUntil": "Zugang bis {date}",
    "pendingPayment": "Zahlung läuft",
    "comingSoon": "Bald verfügbar",
    "draft": "Entwurf",
    "scheduled": "Geplant für {date}"
  },
  "en": {
    "perMonth": "/ month",
    "active": "Active",
    "pending": "Pending",
    "cancelled": "Cancelled",
    "subscribe": "Join",
    "manage": "Manage",
    "accessUntil": "Access until {date}",
    "pendingPayment": "Payment pending",
    "comingSoon": "Coming soon",
    "draft": "Draft",
    "scheduled": "Scheduled for {date}"
  }
}
</i18n>

<template>
  <article
    class="subscription-card"
    :class="{
      'subscription-card--active': reward.activePledge,
      'subscription-card--pending': reward.isPending,
      'subscription-card--cancelled': reward.isCancelled,
      'subscription-card--draft': reward.status === 'draft',
      'subscription-card--scheduled': reward.status === 'scheduled'
    }"
  >
    <!-- Status Badge -->
    <div
      v-if="statusBadge"
      class="subscription-card__badge"
      :class="`subscription-card__badge--${statusBadge.type}`"
    >
      {{ statusBadge.text }}
    </div>

    <!-- Image -->
    <div class="subscription-card__image-wrapper">
      <img
        v-if="rewardImage"
        :src="rewardImage"
        :alt="reward.name"
        class="subscription-card__image"
        loading="lazy"
      >
      <div
        v-else
        class="subscription-card__image subscription-card__image--placeholder"
      />
    </div>

    <!-- Content -->
    <div class="subscription-card__content">
      <!-- Title -->
      <h3 class="subscription-card__title">
        {{ reward.name }}
      </h3>

      <!-- Description (truncated) -->
      <div
        v-if="truncatedDescription"
        class="subscription-card__description"
        v-html="truncatedDescription"
      />

      <!-- Access Until (for cancelled) -->
      <p
        v-if="reward.isCancelled && reward.accessUntil"
        class="subscription-card__access-until"
      >
        {{ $t('accessUntil', { date: localeDate(reward.accessUntil, { locale: $i18n.locale }) }) }}
      </p>

      <!-- Footer: Price & Action -->
      <div class="subscription-card__footer">
        <div class="subscription-card__price">
          <span class="subscription-card__price-amount">{{ centToCurrency(reward.amount, 1, 'EUR') }}</span>
          <span class="subscription-card__price-period">{{ $t('perMonth') }}</span>
        </div>
        <a
          v-if="canSubscribe"
          :href="checkoutUrl(reward._id)"
          class="subscription-card__button subscription-card__button--on-product"
        >
          {{ $t('subscribe') }}
        </a>
        <span
          v-else-if="reward.activePledge"
          class="subscription-card__button subscription-card__button--active"
        >
          {{ $t('active') }}
        </span>
        <span
          v-else-if="reward.isPending"
          class="subscription-card__button subscription-card__button--pending"
        >
          {{ $t('pendingPayment') }}
        </span>
        <span
          v-else-if="reward.status === 'draft'"
          class="subscription-card__button subscription-card__button--disabled"
        >
          {{ $t('draft') }}
        </span>
        <span
          v-else-if="reward.status === 'scheduled'"
          class="subscription-card__button subscription-card__button--disabled"
        >
          {{ $t('comingSoon') }}
        </span>
        <span
          v-else
          class="subscription-card__button subscription-card__button--disabled"
        >
          {{ $t('comingSoon') }}
        </span>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'SubscriptionCard',

  props: {
    reward: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapState(['artist']),

    rewardImage() {
      if (!this.artist || !this.reward || !this.reward.image) {
        return ''
      }

      // Legacy string format (stage only)
      if (typeof this.reward.image === 'string') {
        return this.reward.image
      }

      // Standard image object
      return `/services/media/image/${this.artist._id}/${this.reward.image.name}/400/0/image.webp`
    },

    truncatedDescription() {
      if (!this.reward.description) return ''
      const text = this.stripHtml(this.reward.description)
      if (text.length > 100) {
        return text.substring(0, 100) + '...'
      }
      return text
    },

    statusBadge() {
      if (this.reward.activePledge) {
        return { type: 'active', text: this.$t('active') }
      }
      if (this.reward.isPending) {
        return { type: 'pending', text: this.$t('pending') }
      }
      if (this.reward.isCancelled) {
        return { type: 'cancelled', text: this.$t('cancelled') }
      }
      if (this.reward.status === 'draft') {
        return { type: 'draft', text: this.$t('draft') }
      }
      if (this.reward.status === 'scheduled') {
        return { type: 'scheduled', text: this.$t('scheduled', { date: this.formatDate(this.reward.publishDateTime) }) }
      }
      return null
    },

    canSubscribe() {
      return (
        this.reward.status === 'published' &&
        !this.reward.activePledge &&
        !this.reward.isPending &&
        this.artist?.isBoarded &&
        !this.artist?.hasPendingPayment
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.subscription-card {
  position: relative;
  background-color: var(--product-background-color, #1e1b26);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  height: 100%; // Stretch to match tallest card in grid row

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  // Status modifiers
  &--active {
    border: 2px solid var(--primary-color, #4CAF50);
  }

  &--pending {
    opacity: 0.85;
  }

  &--cancelled {
    opacity: 0.7;
  }

  &--draft,
  &--scheduled {
    opacity: 0.6;
  }

  // Badge
  &__badge {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 3;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &--active {
      background: var(--primary-color, #4CAF50);
      color: var(--primary-text-color, #fff);
    }

    &--pending {
      background: #FF9800;
      color: #fff;
    }

    &--cancelled {
      background: #f44336;
      color: #fff;
    }

    &--draft {
      background: #9E9E9E;
      color: #fff;
    }

    &--scheduled {
      background: #2196F3;
      color: #fff;
    }
  }

  // Image - square 1:1 aspect ratio with center-top alignment
  &__image-wrapper {
    position: relative;
    overflow: hidden;
    aspect-ratio: 1 / 1; // Square aspect ratio
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top; // Align image center horizontally, top vertically
    display: block;
    transition: transform 0.3s ease;
    // Minimal fade - only affects bottom 10-15% of image
    -webkit-mask-image: -webkit-gradient(
      linear,
      left top,
      left bottom,
      color-stop(0, rgba(0, 0, 0, 1)),
      color-stop(0.85, rgba(0, 0, 0, 1)),
      color-stop(0.95, rgba(0, 0, 0, 0.5)),
      color-stop(1, rgba(0, 0, 0, 0))
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 85%,
      rgba(0, 0, 0, 0.5) 95%,
      rgba(0, 0, 0, 0) 100%
    );

    // Placeholder styling when no image
    &--placeholder {
      background-color: var(--product-background-color, #1e1b26);
    }
  }

  &:hover &__image {
    transform: scale(1.02);
  }

  // Content
  &__content {
    position: relative;
    z-index: 2;
    padding: 14px;
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--product-background-color, #1e1b26);
    container-type: inline-size; // Enables card-width container queries for the footer
  }

  // Title
  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--product-background-text-color, #fff);
    margin: 0 0 10px 0;
    line-height: 1.3;
    // Truncate to 2 lines
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Description
  &__description {
    font-size: 0.85rem;
    color: var(--product-background-text-color, #ccc);
    line-height: 1.5;
    flex: 1;
    margin-bottom: 14px;
    // Truncate to 2 lines for tighter look
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Access until
  &__access-until {
    font-size: 0.8rem;
    color: #f44336;
    margin: 0 0 14px 0;
    padding: 6px 10px;
    background: rgba(244, 67, 54, 0.2);
    border-radius: 4px;
  }

  // Footer
  &__footer {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap; // Fallback for browsers without container queries
    padding-top: 14px;
    margin-top: auto;

    // Theme-aware border using pseudo-element
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: var(--product-background-text-color, #fff);
      opacity: 0.15;
    }
  }

  // Price
  &__price {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  &__price-amount {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--product-background-text-color, #fff);
    line-height: 1.2;
  }

  &__price-period {
    font-size: 0.7rem;
    color: var(--product-background-text-color, #aaa);
    opacity: 0.8;
  }

  &__button {
    display: block;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: center;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
    border: none;

    &--on-product {
      background: var(--product-background-text-color, #fff);
      color: var(--product-background-color, #fff);

      &:hover {
        opacity: 0.9;
      }
    }

    &--primary {
      background: var(--primary-color, #333);
      color: var(--primary-text-color, #fff);

      &:hover {
        opacity: 0.9;
      }
    }

    &--active {
      background: rgba(76, 175, 80, 0.2);
      color: #4CAF50;
      cursor: default;
    }

    &--pending {
      background: rgba(255, 152, 0, 0.2);
      color: #FF9800;
      cursor: default;
    }

    &--disabled {
      background: rgba(255, 255, 255, 0.1);
      color: var(--product-background-text-color, #999);
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

// Narrow cards (e.g. two-column mobile grid): stack price above a full-width button
@container (max-width: 239px) {
  .subscription-card__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .subscription-card__price {
    flex-direction: row;
    align-items: baseline;
    gap: 5px;
  }

  .subscription-card__button {
    width: 100%;
  }
}

// Mobile adjustments - keep fonts readable, don't shrink too much
@media screen and (max-width: #{$mobile}) {
  .subscription-card {
    &__content {
      padding: 12px;
    }

    &__title {
      font-size: 1rem; // Keep readable on mobile
      margin-bottom: 8px;
    }

    &__description {
      font-size: 0.8rem;
      margin-bottom: 12px;
    }

    &__price-amount {
      font-size: 1.15rem;
    }

    &__footer {
      padding-top: 12px;
    }

    &__button {
      padding: 8px 14px; // Good touch target
      font-size: 0.8rem;
    }

    &__badge {
      font-size: 0.6rem;
      padding: 3px 8px;
    }
  }
}
</style>
